import { DataTable, Column, Skeleton, InputText, InputNumber } from 'primereact';
import React, {useEffect, useState} from "react";
import {Message, MessageProvider, useMessage} from "../../../Commons/Scripts/Componentes/Message";
import {FormContext, SimpleForm} from "../../../Commons/Scripts/Componentes/Form";
import {SelectField} from "../../../Commons/Scripts/Componentes/SelectField";
import {DateField} from "../../../Commons/Scripts/Componentes/DateField";
import {Button2} from "../../../Commons/Scripts/Componentes/Button";
import {ActivosReporteService} from "../Services/activos-reporte-service";
import {formatear2Decimales} from "../../../Commons/Scripts/Utils/formatear";
import {FechaUtils} from "../../../Commons/Scripts/Utils/fechaUtils";
import {useForm} from "../../../Commons/Scripts/Hooks/useForm";
import {getLink} from "../../../Commons/Scripts/Utils/link";

const origin = window.location.origin;

const InputTypes = {
  TEXTO: "texto",
  NUMERO: "numero",
  MONEDA: "moneda",
};

function InputField(props) {
  const { name, label, disabled, type = InputTypes.TEXTO, noLabel, ...otrasProps } = props;
  const { value, setValue } = React.useContext(FormContext);

  if (!Object.values(InputTypes).includes(type)) {
    throw new Error(`Invalid input type: ${type}`);
  }

  const inputValue = value(name);
  const controlledValue = inputValue === undefined ? "" : inputValue;

  return (
    <div className='custom-InputField'>
      <div className="label">
        <label htmlFor={name}>{label}</label>
      </div>
      <div className="control">
        {type === InputTypes.TEXTO && (
            <input
              type="text"
              value={controlledValue}
              onChange={(e) => {
                setValue(name, e.target.value);
              }}
              disabled={disabled}
            />
        )}
        {type === InputTypes.NUMERO && (
            <input
              type="text"
              className="citrus-input-integer"
              data-type="integer"
              data-toggle="citrus-input"
              value={controlledValue}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (inputValue === "" || !isNaN(parseInt(inputValue, 10))) {
                  setValue(name, inputValue);
                }
              }}
            />
        )}
      </div>
    </div>
  );
}

function BodyCuenta(data, field, setExpandedRows, esNumero = false) {
  return (
    <div
      style={{ width: "100%", cursor: "pointer" }}
      onClick={() => {
        const key = data[field];

        setExpandedRows((prev) => {
          if (prev?.some((p) => p[field] === key)) {
            return prev?.filter((p) => p[field] !== key);
          } else {
            return [...(prev || []), data];
          }
        });
      }}
    >
      {esNumero && <span>{formatear2Decimales(data[field])}</span>}
      {!esNumero && data[field]}
    </div>
  );
}

function TablaReporteDocumentosBancarios({ reporte, loading }) {
  const { useState } = React;

  const [expandedRows, setExpandedRows] = useState(null);

  const grupoCuentas = reporte?.reduce((acc, item) => {
    if (!acc.some((cuenta) => cuenta.CuentaBancaria === item.CuentaBancaria)) {
      return [...acc, item];
    }
    return acc;
  }, []);

  const rowExpansionTemplate = (item) => {
    const filtroCuentas = reporte.filter((d) => d.CuentaBancaria == item.CuentaBancaria);

    function crearColumna({ header, field, footer = (item) => null, esLink = false, alignRight = false }) {
      const esNumerico = typeof filtroCuentas[0][field] === "number" && field !== "IdSec";

      const style = {
        verticalAlign: "middle",
        textAlign: esNumerico ? "right" : alignRight ? "right" : undefined,
        minWidth: esNumerico ? "90px" : undefined,
      };

      return (
        <Column
          key={field}
          field={field}
          header={header}
          body={(item) => {
            if (esLink) {
              return (
                <a className="link" href={getLink(item.Documento, item.IdSec)} target="_blank">
                  {item[field]}
                </a>
              );
            }

            if (!esNumerico) {
              return <span>{item[field]}</span>;
            }
            return <span>{formatear2Decimales(item[field])}</span>;
          }}
          style={style}
          headerClassName={esNumerico ? "alinear-derecha" : alignRight ? "alinear-derecha" : ""}
          footer={footer}
        />
      );
    }

    const columnas = [
      crearColumna({
        header: "Documento",
        field: "Documento",
        esLink: true,
        footer: () => <span>Total</span>,
      }),
      crearColumna({ header: "ID", field: "IdSec", esLink: true, alignRight: true }),
      crearColumna({ header: "Número", field: "Numero", esLink: true, alignRight: true }),
      crearColumna({ header: "Fecha", field: "Fecha", alignRight: true }),
      crearColumna({ header: "Beneficiario", field: "Beneficiario" }),
      crearColumna({ header: "Descripción", field: "Descripcion" }),
      crearColumna({ header: "Estatus", field: "Estatus" }),
      crearColumna({
        header: "Crédito",
        field: "Credito",
        footer: () => {
          const total = filtroCuentas.reduce((acc, item) => acc + item.Credito, 0);
          return (
            <div>
              {filtroCuentas?.[0]?.MonedaSimbolo} {formatear2Decimales(total)}
            </div>
          );
        },
      }),
      crearColumna({
        header: "Débito",
        field: "Debito",
        footer: () => {
          const total = filtroCuentas.reduce((acc, item) => acc + item.Debito, 0);
          return (
            <div>
              {filtroCuentas?.[0]?.MonedaSimbolo} {formatear2Decimales(total)}
            </div>
          );
        },
      }),
    ];

    return <DataTable value={filtroCuentas}>{columnas}</DataTable>;
  };

  if (loading) {
    return (
      <DataTable showHeaders={false} value={Array.from({ length: 30 })}>
        <Column body={<Skeleton style={{ height: "40px" }} />} />
      </DataTable>
    );
  }

  return (
    <DataTable
      id="grid"
      value={grupoCuentas}
      emptyMessage="No se han encontrado resultados"
      sortMode="multiple"
      rowExpansionTemplate={rowExpansionTemplate}
      expandedRows={expandedRows}
      onRowToggle={(e) => setExpandedRows(e.data)}
    >
      <Column
        style={{ width: "3rem" }}
        expander
        body={(rowData) => {
          const isExpanded = expandedRows?.some((p) => p.CuentaBancaria === rowData.CuentaBancaria);

          return (
            <div
              onClick={() => {
                const key = rowData.CuentaBancaria;

                setExpandedRows((prev) => {
                  if (prev?.some((p) => p.CuentaBancaria === key)) {
                    return prev?.filter((p) => p.CuentaBancaria !== key);
                  } else {
                    return [...(prev || []), rowData];
                  }
                });
              }}
              className={`p-row-toggler ${isExpanded ? "expanded" : ""}`}
            >
              <i className="pi pi-chevron-right"></i>
            </div>
          );
        }}
      />
      <Column
        field="CuentaBancaria"
        style={{ minWidth: "8rem", verticalAlign: "middle" }}
        body={(item) => BodyCuenta(item, "CuentaBancaria", setExpandedRows)}
        header="Cuenta de Banco"
        sortable
      />
    </DataTable>
  );
}

function Formulario({
  loading,
  tiposDocumento,
  estatusDocumento,
  periodos,
  bancos,
  bancoDetalles,
  beneficiarios,
  onSubmit,
  valorInicial = {},
  onChange,
}) {
  const form = useForm(valorInicial);

  const handleSubmit = () => {
    onSubmit(form.values);
  };

  const handleChange = (values) => {
    onChange(values);
  };

  return (
    <SimpleForm formulario={form} onChange={handleChange}>
      <div className="row clearfix">
        <SelectField
          name="tipoDocumento"
          opciones={tiposDocumento || []}
          placeholder="Todos"
          label="Documento"
          isLoading={loading}
          opcionPorDefecto={true}
        />
      </div>
      <div className="row clearfix">
        <SelectField
          isMulti
          name="estatusDocumento"
          opciones={estatusDocumento || []}
          placeholder="Todos"
          label="Estatus"
          isLoading={loading}
          width="400px"
        />
      </div>
      <div className="row clearfix">
        <InputField name="id" label="Id" type="numero" />
        <InputField name="numero" label="Número" isRow />
      </div>
      <div className="row clearfix">
        <SelectField
          name="periodo"
          opciones={periodos || []}
          placeholder="Todos"
          label="Período"
          isLoading={loading}
          opcionPorDefecto
        />
      </div>
      <div className="row clearfix">
        <DateField name="fechaInicio" label="Fecha Inicio" mostrarIcono />
        <DateField name="fechaFin" label="Fecha Fin" mostrarIcono />
      </div>
      <div className="row clearfix">
        <SelectField
          name="banco"
          opciones={bancos || []}
          placeholder="Todos"
          label="Banco"
          isLoading={loading}
          opcionPorDefecto
          width="400px"
        />
        <SelectField
          name="bancoDetalle"
          opciones={bancoDetalles || []}
          placeholder="Todos"
          label="Cuenta de Banco"
          isLoading={loading}
          opcionPorDefecto
          isRow
          width="400px"
        />
      </div>
      <div className="row clearfix">
        <SelectField
          isMulti
          name="beneficiario"
          opciones={beneficiarios || []}
          placeholder="Todos"
          label="Beneficiario"
          isLoading={loading}
          width="250px"
        />
      </div>
      <div className="buttons">
        <Button2 texto="Buscar" onClick={handleSubmit} />
      </div>
    </SimpleForm>
  );
}

export function ReporteDocuemtosBancarios() {
  const [loadingDatos, setLoadingDatos] = useState(false);
  const [tiposDocumento, setTiposDocumento] = useState();
  const [estatusDocumento, setEstatusDocumento] = useState();
  const [periodos, setPeriodos] = useState();
  const [bancos, setBancos] = useState();
  const [beneficiarios, setBeneficiarios] = useState();
  const [bancoDetalles, setBancoDetalles] = useState();

  const [loading, setLoading] = useState(false);
  const [reporte, setReporte] = useState();

  const [where, setWhere] = useState({
    EsFecha: true,
    FechaInicio: FechaUtils.getMesAntes(),
    FechaFin: new Date(),
    EstatusList: ["N", "C"],
  });

  const { messages, typeMessages, handleException, clearMessages } = useMessage();

  useEffect(() => {
    setLoading(true);
    clearMessages();

    ActivosReporteService.consultarReporteDocumentosBancarios(where)
      .then((res) => {
        setReporte(res?.Resultados);
      })
      .catch((err) => {
        handleException(err);
      })

      .finally(() => {
        setLoading(false);
      });
  }, [where]);

  useEffect(() => {
    setLoadingDatos(true);
    clearMessages();

    Promise.all([
      ActivosReporteService.consultarTiposDocumento(),
      ActivosReporteService.consultarEstatusDocumento(),
      ActivosReporteService.consultarPeriodos(),
      ActivosReporteService.consultarBancos(),
      ActivosReporteService.consultarBancoDetalles(),
      ActivosReporteService.consultarBeneficiarios(),
    ])
      .then((res) => {
        const tiposDocumento = res[0]?.map((x) => ({
          value: x.id,
          label: x.text,
        }));
        const estatusDocumento = res[1]?.map((x) => ({
          value: x.id,
          label: x.text,
        }));
        const periodos = res[2]?.map((x) => ({
          value: x.Value,
          label: x.Label,
        }));
        const bancos = res[3]?.map((x) => ({
          value: x.IdSec,
          label: x.Nombre,
        }));
        const bancoDetalles = res[4]?.map((x) => ({
          value: x.IdSec,
          label: `${x.NumeroCuenta} - ${x.Descripcion}`,
        }));
        const beneficiarios = res[5]?.map((x) => ({
          value: x.id,
          label: x.text,
        }));

        setTiposDocumento(tiposDocumento);
        setEstatusDocumento(estatusDocumento);
        setPeriodos(periodos);
        setBancos(bancos);
        setBancoDetalles(bancoDetalles);
        setBeneficiarios(beneficiarios);
      })
      .catch((err) => {
        handleException(err);
      })
      .finally(() => {
        setLoadingDatos(false);
      });
  }, []);

  return (
    <div>
      <h1 className="title">Documentos Bancarios</h1>
      <hr />
      <Message messages={messages} type={typeMessages} />
      <Formulario
        tiposDocumento={tiposDocumento}
        estatusDocumento={estatusDocumento}
        periodos={periodos}
        bancos={bancos}
        bancoDetalles={bancoDetalles}
        beneficiarios={beneficiarios}
        loading={loadingDatos}
        onSubmit={(formulario) => {
          const where = {
            TipoDocumento: formulario.tipoDocumento?.value != "-1" ? formulario.tipoDocumento?.value : null,
            EstatusList: formulario.estatusDocumento?.map((e) => e.value),
            IdSec: formulario?.id && Number.isInteger(formulario.id) ? formulario.id : undefined,
            Numero: formulario?.numero,
            Periodo: formulario.periodo?.value != "-1" ? formulario.periodo?.value : undefined,
            EsFecha:
              (formulario?.fechaInicio && formulario?.fechaFin) ||
              (formulario.periodo?.value && formulario.periodo?.value != "-1")
                ? true
                : false,
            FechaInicio: formulario?.fechaInicio,
            FechaFin: formulario?.fechaFin,
            BancoIdSec: formulario.banco?.value != "-1" ? formulario.banco?.value : undefined,
            BancoDetalleIdSec: formulario.bancoDetalle?.value != "-1" ? formulario.bancoDetalle?.value : undefined,
            SuplidorNombreList: formulario.beneficiario?.map((e) => e.label),
          };

          setWhere(where);
        }}
        valorInicial={{
          fechaInicio: FechaUtils.getMesAntes(),
          fechaFin: new Date(),
          estatusDocumento: [
            { value: "N", label: "Nuevo" },
            { value: "C", label: "Conciliado" },
          ],
        }}
        onChange={(formulario) => {
          if (formulario.banco?.value) {
            const where = {
              bancoIdSec: formulario.banco?.value != "-1" ? formulario.banco?.value : undefined,
            };

            setLoadingDatos(true);
            ActivosReporteService.consultarBancoDetalles(where)
              .then((res) => {
                const bancoDetalles = res?.map((x) => ({
                  value: x.IdSec,
                  label: `${x.NumeroCuenta} - ${x.Descripcion}`,
                }));
                setBancoDetalles(bancoDetalles);
              })
              .catch((err) => {
                handleException(err);
              })
              .finally(() => {
                setLoadingDatos(false);
              });
          }
        }}
      />
      <br />
      <TablaReporteDocumentosBancarios reporte={reporte} loading={loading} />
    </div>
  );
}